import { useEffect, useState } from "react";
import type { useRadio, RadioMapping } from "../hooks/useRadio";

type Axis = "throttle" | "yaw" | "pitch" | "roll" | "arm" | "rec";
const ACTIONS: { key: Axis; label: string }[] = [
  { key: "throttle", label: "Throttle" },
  { key: "yaw", label: "Yaw" },
  { key: "pitch", label: "Pitch" },
  { key: "roll", label: "Roll" },
  { key: "arm", label: "Arm" },
  { key: "rec", label: "Record" },
];

/**
 * Connect a radio via WebHID, watch its channels live, and map each channel to a
 * flight axis by "learn" (move the stick — the biggest mover is captured). Same
 * pattern as the memviz SpaceMouse. Mapping persists in localStorage.
 */
export function RadioPanel({ radio, embedded }: { radio: ReturnType<typeof useRadio>; embedded?: boolean }) {
  const { connected, deviceName, count, error, mapping, setMapping, channels, connect } = radio;
  const [disp, setDisp] = useState<number[]>([]);
  const [learning, setLearning] = useState<Axis | null>(null);
  const cls = embedded ? "radio-panel embedded" : "radio-panel";

  useEffect(() => {
    if (!connected) return;
    const id = window.setInterval(() => setDisp([...channels.current]), 80);
    return () => window.clearInterval(id);
  }, [connected, channels]);

  function learn(action: Axis) {
    setLearning(action);
    const base = [...channels.current];
    const start = performance.now();
    let best = -1, bestD = 0.2;
    const iv = window.setInterval(() => {
      const cur = channels.current;
      for (let i = 0; i < cur.length; i++) {
        const d = Math.abs((cur[i] ?? 0) - (base[i] ?? 0));
        if (d > bestD) { bestD = d; best = i; }
      }
      if (performance.now() - start > 1600) {
        window.clearInterval(iv);
        setLearning(null);
        if (best >= 0) setMapping((m) => ({ ...m, [action]: best }));
      }
    }, 60);
  }

  const toggleInv = (a: Axis) =>
    setMapping((m: RadioMapping) => ({ ...m, inv: { ...m.inv, [a]: !m.inv[a] } }));

  if (!connected) {
    return (
      <div className={cls}>
        {!embedded && <div className="radio-title">Radio</div>}
        <button className="chip go" onClick={connect}>🎮 Connect radio (WebHID)</button>
        {error && <div className="err">{error}</div>}
        <div className="radio-hint">EdgeTX / Radiomaster: set the radio to <b>USB Joystick (HID)</b>, plug in, Connect, and pick it.</div>
      </div>
    );
  }

  return (
    <div className={cls}>
      {!embedded && <div className="radio-title">{deviceName} · {count}ch</div>}
      <div className="radio-monitor">
        {disp.map((v, i) => (
          <div className="radio-ch" key={i} title={"Ch " + i}>
            <span className="radio-ci">{i}</span>
            <div className="radio-bar">
              <div className="radio-fill" style={{ width: Math.min(50, Math.abs(v) * 50) + "%", left: v < 0 ? undefined : "50%", right: v < 0 ? "50%" : undefined }} />
            </div>
          </div>
        ))}
      </div>
      <div className="radio-map">
        {ACTIONS.map((a) => (
          <div className="radio-row" key={a.key}>
            <span className="radio-act">{a.label}</span>
            <span className="radio-cur">{mapping[a.key] >= 0 ? "ch" + mapping[a.key] : "—"}</span>
            <button className={"chip mini" + (learning === a.key ? " active" : "")} onClick={() => learn(a.key)}>{learning === a.key ? "move…" : "learn"}</button>
            <button className={"chip mini" + (mapping.inv[a.key] ? " active" : "")} onClick={() => toggleInv(a.key)}>inv</button>
          </div>
        ))}
      </div>
    </div>
  );
}
